import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, CheckCircle, AlertTriangle, Clock, Shield } from 'lucide-react';
import './ProtocolViewModal.css';

const ProtocolViewModal = ({ protocol, onClose }) => {
  const { t } = useTranslation(['protocols', 'common']);
  if (!protocol) return null;

  // Calculate totals
  const totalSteps = protocol.steps?.length || 0;
  const totalActions = protocol.steps?.reduce((sum, step) => sum + (step.actions?.length || 0), 0) || 0;
  const totalDuration = protocol.steps?.reduce((sum, step) => sum + (step.estimatedDuration || 0), 0) || 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="protocol-view-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div>
            <h2>{protocol.name}</h2>
            <div className="protocol-meta">
              <span className={`status-badge status-${protocol.status?.toLowerCase()}`}>
                {protocol.status || t('protocols:viewDraft')}
              </span>
              <span className="version-badge">v{protocol.version || '1.0'}</span>
              {protocol.category && (
                <span className="category-badge">{protocol.category}</span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="btn btn-secondary">
            <X size={20} />
          </button>
        </div>

        {/* Summary Cards */}
        <div className="view-summary">
          <div className="summary-item">
            <div className="summary-icon">
              <CheckCircle size={20} />
            </div>
            <div>
              <div className="summary-value">{totalSteps}</div>
              <div className="summary-label">{t('protocols:viewSteps')}</div>
            </div>
          </div>
          <div className="summary-item">
            <div className="summary-icon">
              <Shield size={20} />
            </div>
            <div>
              <div className="summary-value">{totalActions}</div>
              <div className="summary-label">{t('protocols:viewActions')}</div>
            </div>
          </div>
          <div className="summary-item">
            <div className="summary-icon">
              <Clock size={20} />
            </div>
            <div>
              <div className="summary-value">{totalDuration}</div>
              <div className="summary-label">{t('protocols:viewMinutes')}</div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="modal-content">
          {/* Description */}
          {protocol.description && (
            <div className="view-section">
              <h3>{t('protocols:viewDescription')}</h3>
              <p className="view-text">{protocol.description}</p>
            </div>
          )}

          {protocol.defaultTaskTemplateName && (
            <div className="view-section">
              <h3>{t('protocols:viewDefaultTaskTemplate')}</h3>
              <p className="view-text">{protocol.defaultTaskTemplateName}</p>
            </div>
          )}

          {protocol.applicabilityRules && protocol.applicabilityRules.length > 0 && (
            <div className="view-section">
              <h3>{t('protocols:viewApplicability')}</h3>
              {protocol.applicabilityRules.map((rule, idx) => (
                <div key={idx} className="applicability-item">
                  <strong>{rule.structureType}:</strong>{' '}
                  {rule.selectedEntityNames?.join(', ') || rule.selectedEntities?.join(', ') || t('protocols:viewNotSpecified')}
                </div>
              ))}
            </div>
          )}

          {protocol.linkedRequirements && protocol.linkedRequirements.length > 0 && (
            <div className="view-section">
              <h3>{t('protocols:linkedRequirements') || 'Linked requirements'}</h3>
              <ul className="linked-requirements-list">
                {protocol.linkedRequirements.map((lr, idx) => (
                  <li key={lr.standardRequirementId || idx} className="linked-requirement-item">
                    <span className="linked-req-code">{lr.requirementCode || lr.requirementId}</span>
                    {lr.standardCode && <span className="linked-req-standard">{lr.standardCode}</span>}
                    <span className={`role-badge role-${lr.role || 'supporting'}`}>
                      {lr.role === 'primary' ? (t('protocols:primary') || 'Primary') : (t('protocols:supporting') || 'Supporting')}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {protocol.steps && protocol.steps.length > 0 && (
            <div className="view-section">
              <h3>{t('protocols:viewSteps')} ({totalSteps})</h3>
              {protocol.steps
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((step, idx) => (
                  <div key={idx} className="step-view-item">
                    <div className="step-view-header">
                      <h4>
                        {t('protocols:viewStepTitle', { number: step.order || idx + 1, name: step.name })}
                      </h4>
                      <div className="step-view-meta">
                        {step.estimatedDuration && (
                          <span className="duration-badge">
                            <Clock size={14} />
                            {step.estimatedDuration} {t('protocols:min')}
                          </span>
                        )}
                        {step.isRequired !== undefined && (
                          <span className={`required-badge ${step.isRequired ? 'required' : 'optional'}`}>
                            {step.isRequired ? t('protocols:viewRequired') : t('protocols:viewOptional')}
                          </span>
                        )}
                        {step.taskTemplateName && (
                          <span className="template-badge">
                            📋 {step.taskTemplateName}
                          </span>
                        )}
                      </div>
                    </div>
                    {step.description && (
                      <p className="step-description">{step.description}</p>
                    )}

                    {/* Actions */}
                    {step.actions && step.actions.length > 0 && (
                      <div className="actions-view">
                        <strong>{t('protocols:viewActions')} ({step.actions.length}):</strong>
                        <ul className="actions-list">
                          {step.actions
                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                            .map((action, actionIdx) => (
                              <li key={actionIdx} className="action-view-item">
                                <span className="action-name">
                                  {action.order || actionIdx + 1}. {action.name}
                                </span>
                                {action.estimatedDuration && (
                                  <span className="action-duration">
                                    {action.estimatedDuration} {t('protocols:min')}
                                  </span>
                                )}
                                {action.taskTemplateName && (
                                  <span className="action-template">
                                    📋 {action.taskTemplateName}
                                  </span>
                                )}
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">
            {t('common:close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProtocolViewModal;

